import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { Button, Spinner, ValidatedInput } from '../../components'
import CountryPickerModal from '../CountryPickerModal'
import countries from '../../countries.json'
import { isPasswordValid, isTelephoneValid } from '../../helpers'
import { appsLoginSelected, defaultPhoneCountryCode } from '../../helpers/app'
import { ICountry } from '../../models/country'
import { LiteralsService } from '../../services'
import {
  clearSignInErrorAction,
  setSignInCallingCodeAction,
  setSignInCountryAction,
  setSignInPasswordAction,
  setSignInUsernameAction,
  signInAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'

const isPhoneLogin = !!appsLoginSelected?.login.includes('phone') && !appsLoginSelected?.login.includes('email')

interface IOwnProps {
  // The host decides where each link leads: another page, or another step inside a modal.
  onForgotPassword: () => void
  onVerification: () => void
  onSignUp: () => void
  onGuest: () => void
  // Pre-redesign actions: just the button and the sign up link.
  classic?: boolean
}

interface IProps extends IOwnProps, DispatchProp<AnyAction> {
  language: string
  username: string
  password: string
  isSigningIn: boolean
  error: boolean
  country?: ICountry
}

/**
 * The sign in form — credentials, the button and the links around it — on its own, so the sign
 * in page and the prompt shown to guests render the very same thing. The host puts the chrome
 * (card, logo, title, Google) around it.
 */
class SignInForm extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props)
    if (isPhoneLogin && !props.country) {
      const defaultCountry = countries.find(country => country.code === defaultPhoneCountryCode)
      if (defaultCountry) {
        props.dispatch(setSignInCountryAction(defaultCountry))
        props.dispatch(setSignInCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get isUsernameValid(): boolean {
    if (isPhoneLogin) return isTelephoneValid(this.props.username)
    return this.props.username.length > 0
  }

  private get signInAlert(): ReactNode {
    if (!this.props.error) return null
    return (
      <div className="error-container">
        <span className="error">
          { LiteralsService.get('userOrPasswordWrong', true) }
        </span>
      </div>
    )
  }

  private get buttonIsDisabled(): boolean {
    const { password, isSigningIn } = this.props

    return !this.isUsernameValid || !isPasswordValid(password) || isSigningIn
  }

  private get signInButton(): ReactNode {
    if (this.props.isSigningIn) return <div className="loading"><Spinner /></div>
    return (
      <Button
        title={ LiteralsService.get('continue') }
        onPress={ this.signIn }
        filled
        upper
        isDisabled={ this.buttonIsDisabled }
      />
    )
  }

  private get actions(): ReactNode {
    const { classic, onVerification, onSignUp, onGuest } = this.props

    if (classic) {
      return (
        <section className="actions">
          { this.signInButton }
          <div className="link sign-up">
            <span onClick={ onSignUp }>
              { LiteralsService.get('notUserCreateOne', true) }
            </span>
          </div>
        </section>
      )
    }

    return (
      <section className="actions">
        { this.signInButton }
        <div className="verification">
          <span>{ LiteralsService.get('inputVerificationCode') }</span>
          <span className="go" onClick={ onVerification }>
            { LiteralsService.get('inputVerificationCodeSubtitle') }
          </span>
        </div>
        <div className="divider">
          { LiteralsService.get('noAccountYet') }
        </div>
        <div className="secondary-actions">
          <span onClick={ onSignUp }>
            { LiteralsService.get('createYourAccount').toUpperCase() }
          </span>
          <span onClick={ onGuest }>
            { LiteralsService.get('guestMode').toUpperCase() }
          </span>
        </div>
      </section>
    )
  }

  public render(): ReactNode {
    const { username, password, error, country, onForgotPassword } = this.props

    return (
      <>
        <section className="fields">
          {
            isPhoneLogin ? (
              <ValidatedInput
                name="username"
                phone
                label={ LiteralsService.get('phone', true) }
                value={ username }
                country={ country }
                onChange={ this.onUsernameChange }
                error={ !this.isUsernameValid || error }
              />
            ) : (
              <ValidatedInput
                name="username"
                label={ LiteralsService.get('emailOrUsername', true) }
                value={ username }
                onChange={ this.onUsernameChange }
                error={ !this.isUsernameValid || error }
              />
            )
          }
          <ValidatedInput
            name="password"
            label={ LiteralsService.get('password', true) }
            value={ password }
            onChange={ this.onPasswordChange }
            isPassword
            error={ !isPasswordValid(password) || error }
          />
          <div className="link forgotten-password">
            <span onClick={ onForgotPassword }>
              { LiteralsService.get('forgottenPassword', true) }
            </span>
          </div>
          { this.signInAlert }
        </section>
        { isPhoneLogin && <CountryPickerModal selectCountry={ this.onCountryChange } selectedCountry={ this.props.country } /> }
        { this.actions }
      </>
    )
  }

  private onUsernameChange = (username: string) => {
    const { error, dispatch } = this.props

    if (error) dispatch(clearSignInErrorAction())
    dispatch(setSignInUsernameAction(username))
  }

  private onPasswordChange = (password: string) => {
    const { error, dispatch } = this.props

    if (error) dispatch(clearSignInErrorAction())
    dispatch(setSignInPasswordAction(password))
  }

  private onCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setSignInCountryAction(country))
    dispatch(setSignInCallingCodeAction(country.dial_code))
  }

  private signIn = () => {
    this.props.dispatch(signInAction())
  }
}

const mapStateToProps = ({ signIn, configuration }: IStoreState) => ({
  // Redraws the literals when the language selector changes the language.
  language: configuration.language,
  username: signIn.username,
  password: signIn.password,
  isSigningIn: signIn.isSigningIn,
  error: signIn.error,
  country: signIn.country
})

export default connectRedux(mapStateToProps, SignInForm) as React.ComponentType<IOwnProps>
